import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Sparkles, Loader2 } from "lucide-react";
import PageHeader from "@/components/business/PageHeader";

export default function Plans() {
  const [data, setData] = useState({ plans: [], addons: [] });
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [{ data: d }, { data: m }] = await Promise.all([
      api.get("/subscription/plans"),
      api.get("/subscription/me"),
    ]);
    setData(d); setMe(m);
  };
  useEffect(() => { load(); }, []);

  const activate = async (planKey) => {
    setBusy(planKey);
    try {
      await api.put("/subscription/plan", { plan_key: planKey, addons: me?.subscription?.addons || [] });
      toast.success("Plan updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const toggleAddon = async (addonKey) => {
    setBusy(addonKey);
    try {
      const current = me?.subscription?.addons || [];
      const next = current.includes(addonKey) ? current.filter(x => x !== addonKey) : [...current, addonKey];
      await api.put("/subscription/plan", { plan_key: me?.subscription?.plan_key, addons: next });
      toast.success("Addons updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (!me) return <AppShell><Loader2 className="h-5 w-5 animate-spin" /></AppShell>;
  const activePlan = me.subscription.plan_key;
  const activeAddons = new Set(me.subscription.addons || []);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Plans &amp; Add-ons</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Every plan gets AI-assisted preparation. Above <span className="font-semibold">Self Filing</span>, every filing is reviewed and approved by a Chartered Accountant before submission.
        </p>
        {me.effective.role_override && (
          <div className="mt-3 rounded border border-blue-300 bg-blue-50 p-3 text-xs">CA / Admin account — you're entitled to the full <span className="font-semibold">CA Practice Platform</span> automatically.</div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.plans.map(p => {
            const isActive = p.key === activePlan;
            return (
              <Card key={p.key} className={`p-6 border-border flex flex-col ${isActive ? "ring-2 ring-primary" : ""}`} data-testid={`plan-${p.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-lg">{p.name}</div>
                  {isActive && <Badge className="bg-primary">Active</Badge>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground min-h-[40px]">{p.description}</p>
                <div className="mt-3">
                  <div className="text-3xl font-display font-bold">₹{Number(p.price_annual_inr).toLocaleString()}<span className="text-sm text-muted-foreground">/year</span></div>
                  {p.price_monthly_inr > 0 && <div className="text-[10px] text-muted-foreground">or ₹{p.price_monthly_inr}/month</div>}
                </div>
                <ul className="mt-4 space-y-1.5 text-xs flex-1">
                  {Object.entries(p.entitlements).slice(0, 8).map(([k, v]) => (
                    <li key={k} className="flex items-start gap-2">
                      {v ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground/40 mt-0.5 flex-shrink-0" />}
                      <span className={v ? "" : "text-muted-foreground line-through"}>{k.replace(/_/g, " ")}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 text-[10px] text-muted-foreground">
                  Limits: {p.limits.returns_per_year} returns/yr · {p.limits.ai_messages_per_month} AI msgs/mo · {Math.round((p.limits.storage_bytes || 0) / (1024 * 1024 * 1024))} GB
                </div>
                <Button
                  className="mt-4 w-full"
                  variant={isActive ? "outline" : "default"}
                  disabled={isActive || busy === p.key || me.effective.role_override}
                  onClick={() => activate(p.key)}
                  data-testid={`activate-${p.key}`}
                >{isActive ? "Current plan" : busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Choose plan"}</Button>
              </Card>
            );
          })}
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Add-ons</h2>
          <p className="mt-1 text-sm text-muted-foreground">Optional feature packs. Toggle any time.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.addons.map(a => {
              const on = activeAddons.has(a.key);
              return (
                <Card key={a.key} className={`p-4 border-border ${on ? "ring-2 ring-primary" : ""}`} data-testid={`addon-${a.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{a.name}</div>
                    {on && <Badge className="bg-primary text-[10px]">On</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">₹{a.price_monthly_inr}/mo · unlocks <code>{a.entitlement}</code></div>
                  <Button
                    className="mt-3 w-full"
                    variant={on ? "outline" : "default"}
                    size="sm"
                    disabled={busy === a.key || me.effective.role_override}
                    onClick={() => toggleAddon(a.key)}
                  >{busy === a.key ? <Loader2 className="h-4 w-4 animate-spin" /> : (on ? "Remove" : "Add")}</Button>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
